import axios, { AxiosRequestConfig } from "axios";

const baseURL = process.env.VUE_APP_API_URL;

class WalletService {
  static buildApiUrlAndParams(
    url: string,
    params: Record<string, string | number | object | undefined | null> = {}
  ): { url: string } {
    const searchFilters = params.searchFilters;
    if (
      searchFilters &&
      typeof searchFilters === "object" &&
      !Array.isArray(searchFilters)
    ) {
      Object.assign(params, searchFilters);
      delete params.searchFilters;
    }
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value.toString().trim() !== ""
      ) {
        queryParams.append(key, value.toString().trim());
      }
    });
    const queryString = queryParams.toString();
    return {
      url: queryString ? `${url}?${queryString}` : url,
    };
  }

  static getWalletsMainGrid(
    params: Record<string, string | number | undefined | null> = {}
  ) {
    const paramsCopy = { ...params };
    const url = `${baseURL}/wallet/portfolio-management/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);
    return axios.get(finalUrl);
  }

  static getWalletOperationsLog(
    params: Record<string, string | number | object | undefined | null> = {}
  ) {
    const walletId = params.walletId;
    const paramsCopy = { ...params };
    delete paramsCopy.walletId;
    let url = `${baseURL}/wallet/${walletId}/operations-log/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);

    return axios.get(finalUrl);
  }

  static getFinancialOperationsLog(
    params: Record<string, string | number | object | undefined | null> = {}
  ) {
    const walletId = params.walletId;
    const paramsCopy = { ...params };
    delete paramsCopy.walletId;

    if (paramsCopy.financial_operations == "True") {
      paramsCopy.type = 'financial-operations';
      delete paramsCopy.financial_operations;
    }

    const url = `${baseURL}/wallet/${walletId}/operations-log/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);

    return axios.get(finalUrl);
  }

  static getDonorWallets(
    params: Record<string, string | number | object | undefined | null> = {}
  ) {
    const paramsCopy = { ...params };
    const url = `${baseURL}/wallet/donor-wallets/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);

    return axios.get(finalUrl);
  }

  static getWalletStatisticsData(
    params: Record<string, string | number | object | undefined | null> = {}
  ) {
    const walletId = params.walletId;
    const paramsCopy = { ...params };
    delete paramsCopy.walletId;
    let url = `${baseURL}/wallet/${walletId}/stats/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);

    return axios.get(finalUrl);
  }

  static getWalletsOperationsLogsDropdown(wallet_type: string | null = null) {
    const url = `${baseURL}/wallet/operations-log/filters`;
    const params = wallet_type ? { wallet_type } : {};
    return axios.get(url, { params });
  }

  static getDonorWalletTopUpTransactions({
    walletId,
    page,
    page_size,
    order_by,
    sort,
    export_file,
  }: any) {
    let url = `${baseURL}/wallet/donor-wallet/${walletId}/incoming-transactions/?page=${page}&page_size=${page_size}`;
    url += order_by ? `&order_by=${order_by}` : "";
    url += sort ? `&sort=${sort}` : "";
    url += export_file ? `&export=${export_file}` : "";

    return axios.get(url);
  }

  static getDonorWalletLogs(id: any) {
    const url = `${baseURL}/wallet/donor-wallet/${id}/logs`;

    return axios.get(url);
  }

  static getDonorWalletDetails(id: any) {
    const url = `${baseURL}/wallet/donor-wallet/${id}`;

    return axios.get(url);
  }

  static donateFromDonorWallet(id: any, payload: any) {
    const url = `${baseURL}/wallet/donor-wallet/${id}/`;

    return axios.post(url, payload);
  }

  static getZakatWallet() {
    const url = `${baseURL}/wallet/zakat-wallet/`;

    return axios.get(url);
  }

  static createWallet(requestBody: any = {}) {
    const url = `${baseURL}/wallet/create/`;

    return axios.post(url, requestBody);
  }

  static updateWallet(requestBody: any = {}, walletId: number | null = null) {
    const url = walletId
      ? `${baseURL}/wallet/update/${walletId}`
      : `${baseURL}/wallet/create/`;

    return axios.patch(url, requestBody);
  }

  static createDonorWallet(requestBody: any = {}) {
    const url = `${baseURL}/wallet/create-donor-wallet/`;

    return axios.post(url, requestBody);
  }

  static getSpecialties() {
    const url = `${baseURL}/charity/cases/medical-data/dropdown`;

    return axios.get(url);
  }

  static transferFromWallet(requestBody: any = {}) {
    const url = `${baseURL}/wallet/transfer-money/`;

    return axios.post(url, requestBody);
  }

  static getCases(id = 0, quickDonation = false) {
    let url = `${baseURL}/wallet/get-to-wallets/?`;
    url += quickDonation ? `quick-donation=1` : `source=${id}`;

    return axios.get(url);
  }

  static getAllCasesDonationWalletId() {
    const url = `${baseURL}/wallet/all-cases-wallet/`;

    return axios.get(url);
  }

  static getWalletDetails(id: number, walletType: string) {
    let url = `${baseURL}/wallet/get-wallet/${id}`;
    if (walletType) {
      url += `?wallet_type=${walletType}`;
    }
    return axios.get(url);
  }

  static updateWalletDetails(id: number, name: string) {
    const url = `${baseURL}/wallet/update/${id}`;

    return axios.patch(url, { name });
  }

  static updateDonorWalletDetails(
    wallet_id: number,
    wallet_name: string,
    email: string,
    mobile_number: string
  ) {
    const url = `${baseURL}/wallet/create-donor-wallet/`;

    return axios.patch(url, { wallet_id, wallet_name, email, mobile_number });
  }

  static getMajorDonors(limit: number = 10, offset: number = 0) {
    const url = `${baseURL}/wallet/top-donors-web/?limit=${limit}&offset=${offset}`;

    return axios.get(url);
  }

  static deleteWallet(id: number) {
    const url = `${baseURL}/wallet/delete/${id}`;

    return axios.delete(url);
  }
  static topUpWallet(id: number, payload: any) {
    const url = `${baseURL}/wallet/transaction/${id}/create/`;
    return axios.post(url, payload);
  }

  static getReinforcementsList(
    params: Record<string, string | number | object | undefined | null> = {}
  ) {
    const url = `${baseURL}/wallet/reinforcements/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, params);
    return axios.get(finalUrl);
  }

  static deleteReinforcement(id: number) {
    const url = `${baseURL}/wallet/reinforcements/${id}/delete/`;
    return axios.delete(url);
  }

  static getReinforcementDetail(id: number | string) {
    const url = `${baseURL}/wallet/reinforcements/${id}/`;
    return axios.get(url);
  }

  static getReinforcementForEdit(id: number | string) {
    const url = `${baseURL}/wallet/reinforcements/${id}/edit/`;
    return axios.get(url);
  }

  static patchReinforcement(id: number | string, payload: Record<string, unknown>) {
    const url = `${baseURL}/wallet/reinforcements/${id}/edit/`;
    return axios.patch(url, payload);
  }

  static getPrivateWalletDetail(id: number | string) {
    const url = `${baseURL}/wallet/private/${id}/detail/`;
    return axios.get(url);
  }

  static getPrivateWalletForEdit(id: number | string) {
    const url = `${baseURL}/wallet/private/${id}/edit/`;
    return axios.get(url);
  }

  static patchPrivateWallet(id: number | string, payload: Record<string, any>) {
    const url = `${baseURL}/wallet/private/${id}/edit/`;
    return axios.patch(url, payload);
  }

  static getDonationCases(params: Record<string, any> = {}) {
    const walletId = params.walletId;
    const paramsCopy: Record<string, any> = {
      source: walletId,
      page: params.page,
      page_size: params.page_size,
      order_by: params.order_by,
      sort: params.sort,
    };

    const searchFilters = params.searchFilters || {};
    if (searchFilters.id) paramsCopy.case_number = searchFilters.id;
    if (searchFilters.charity) paramsCopy.case_charity = searchFilters.charity;
    if (searchFilters.specialty) paramsCopy.case_specialty = searchFilters.specialty;
    if (searchFilters.region) paramsCopy.region = searchFilters.region;
    if (searchFilters.case_action_description) {
      paramsCopy.case_action_description = Array.isArray(searchFilters.case_action_description)
        ? searchFilters.case_action_description.join(',')
        : searchFilters.case_action_description;
    }

    const url = `${baseURL}/wallet/donation-cases/`;
    const { url: finalUrl } = this.buildApiUrlAndParams(url, paramsCopy);
    return axios.get(finalUrl);
  }

  static getReinforcementWallets(search?: string) {
    const url = `${baseURL}/wallet/reinforcements/wallets/`;
    return axios.get(url, { params: search ? { search } : {} });
  }

  static createReinforcement(payload: { wallet: number; reinforcements: any[] }) {
    const url = `${baseURL}/wallet/reinforcements/create/`;
    return axios.post(url, payload);
  }

  static getAdvanceDonationConfig(sourceWalletId: number | string) {
    const url = `${baseURL}/wallet/advance-donation/config/?source=${sourceWalletId}`;
    return axios.get(url);
  }

  static previewAdvanceDonation(payload: any, params: any = {}) {
    const url = `${baseURL}/wallet/advance-donation/preview/`;
    return axios.post(url, payload, { params });
  }

  static sendAdvanceDonationOtp() {
    const url = `${baseURL}/wallet/advance-donation/send-otp/`;
    return axios.post(url);
  }

  static confirmAdvanceDonation(payload: any) {
    const url = `${baseURL}/wallet/advance-donation/confirm/`;
    return axios.post(url, payload);
  }

  static caseSpecialties() {
    const url = `${baseURL}/charity/case-specialties`;
    return axios.get(url);
  }
}

export default WalletService;

export enum WALLET_TYPES {
  Most_Urgent = "الأشد حاجة",
  Waqf = "وقف",
  Auto = "تلقائي",
  Custom = "مخصص",
  Internal = "داخلي",
}
